// ---------------------------------------------------------------------------
// SSR / proxy response cache engine (server-only, in-memory per PM2 process).
// Trello 430 RATE LIMIT, WP-13 (a)(b). Owner decision D-29 A (15 Okt 2026).
//
// Why this exists. Every SSR render and every Next API proxy call reaches the
// Laravel backend from ONE IP (the prod-fe host), so the backend's per-route
// guest throttle (60/min) is shared by the whole visitor base. 14,808 429s in
// 30 days on `static-content/premium-landing-page` (peak 574-669/min) came
// from: a cache stampede at every TTL expiry (no single-flight), stale entries
// that re-fired a refresh on EVERY hit while the backend was answering 429
// (a refresh loop), a key space the client could inflate (`?c=<random>`), and
// a key that ignored the visitor's country.
//
// Contract of `ssrCachedFetch(options, loader)`:
//   * fresh (age <= ttl)                 -> served, no backend call.
//   * stale (ttl < age <= ttl + swr)     -> served instantly, ONE background
//                                           refresh (single-flight) and, after
//                                           a failed refresh, exponential
//                                           backoff (Retry-After honoured) so a
//                                           failing backend is not hammered.
//   * expired / missing                  -> ONE shared loader call (concurrent
//                                           callers join it). If it fails and a
//                                           copy younger than `staleOnErrorMs`
//                                           exists AND the scope allows it AND
//                                           the error is backend trouble (no
//                                           HTTP status, 408, 429, >= 500), that
//                                           copy is served (after `scrub`),
//                                           else the error propagates exactly
//                                           as before (404/410/422 never serve
//                                           a stale copy).
//   * a 429 additionally opens a short fail-fast window for the key so the
//     callers do not each re-ask the backend while it is throttling us.
//
// The cache never serves a response across keys: callers build keys from every
// input that changes the backend answer (see landingPageSsr.js).
// ---------------------------------------------------------------------------
import { getRetryAfterSeconds, isRateLimitError } from "./rateLimit";

// Only real backend trouble may be answered from an expired copy: no HTTP status
// at all (network error, timeout), 408, 429 or >= 500. Any other status (404,
// 410, 422, ...) is an answer about the request itself and propagates exactly
// as it did before the cache existed.
const errorStatus = (err) => {
  if (!err || typeof err !== "object") return NaN;
  const direct = Number(err.status || err.statusCode);
  if (direct) return direct;
  return Number(err.response && err.response.status);
};

export const isBackendTroubleError = (err) => {
  const status = errorStatus(err);
  if (!status) return true; // NaN or 0: no HTTP status
  return status === 408 || status === 429 || status >= 500;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

const readNumberEnv = (name, fallback) => {
  const raw = process.env[name];
  if (raw === undefined || raw === null || raw === "") return fallback;
  const value = Number(raw);
  return isFinite(value) && value >= 0 ? value : fallback;
};

// Single clock for the engine so tests can drive time (Babel's corejs2 runtime
// pins Date.now at load, a spy on it would not reach this module).
let clockOverride = null;
const nowMs = () => (clockOverride ? clockOverride() : Date.now());
export const __setSsrCacheClockForTests = (fn) => { clockOverride = fn || null; };

// Knobs. Defaults reproduce the pre-WP-13 windows (15 min fresh + 60 min
// stale-while-revalidate, 300 entries); everything new is bounded and has an
// env override so ops can retune without a deploy.
export const ssrCacheConfig = () => ({
  ttlMs: readNumberEnv("SSR_CACHE_TTL_MS", 15 * MINUTE_MS),
  swrMs: readNumberEnv("SSR_CACHE_STALE_MS", HOUR_MS),
  // D-29 A: hard ceiling on how old a copy may be when it is served because
  // the backend is failing. Proposed 6 h by the plan.
  maxStaleOnErrorMs: readNumberEnv("SSR_CACHE_MAX_STALE_ON_ERROR_MS", 6 * HOUR_MS),
  maxEntries: Math.max(10, readNumberEnv("SSR_CACHE_MAX_ENTRIES", 300)),
  // Entries keyed by a client-supplied id (promo id, collab slug, ...) share
  // this smaller budget so they can never push the hot base entries out.
  maxVariantEntries: Math.max(1, readNumberEnv("SSR_CACHE_MAX_VARIANT_ENTRIES", 100)),
  // TTL is shortened by a random 0-15% (never lengthened, so max staleness
  // never exceeds today's) so entries that were filled together (boot
  // pre-warm, a restart) do not all expire in the same second.
  ttlJitter: Math.min(0.5, readNumberEnv("SSR_CACHE_TTL_JITTER", 0.15)),
  refreshBackoffBaseMs: readNumberEnv("SSR_CACHE_REFRESH_BACKOFF_BASE_MS", 5000),
  refreshBackoffMaxMs: readNumberEnv("SSR_CACHE_REFRESH_BACKOFF_MAX_MS", 5 * MINUTE_MS),
  // Fail-fast window after a 429 when the backend sent no Retry-After.
  rateLimitDefaultWaitMs: readNumberEnv("SSR_CACHE_429_DEFAULT_WAIT_MS", 5000),
  rateLimitMaxWaitMs: readNumberEnv("SSR_CACHE_429_MAX_WAIT_MS", 30 * 1000),
});

const entries = new Map(); // key -> entry; insertion order == LRU order (oldest first)
const inflight = new Map(); // key -> Promise (single-flight)
const failFast = new Map(); // key -> { until, error } (429 only)
let variantCount = 0;

const counters = {
  hit: 0,
  staleHit: 0,
  miss: 0,
  coalesced: 0,
  refresh: 0,
  refreshFailed: 0,
  staleOnError: 0,
  errorPropagated: 0,
  failFast: 0,
  bypass: 0,
  evicted: 0,
};

const bump = (name) => {
  counters[name] += 1;
  startStatsLogger();
};

export const getSsrCacheStats = () => ({
  ...counters,
  size: entries.size,
  variants: variantCount,
  inflight: inflight.size,
});

/** Callers that decided a request is not cacheable (invalid input) record it. */
export const noteSsrCacheBypass = () => bump("bypass");

const dropEntry = (key) => {
  const entry = entries.get(key);
  if (!entry) return;
  entries.delete(key);
  if (entry.variant) variantCount -= 1;
};

const touch = (key, entry) => {
  entries.delete(key);
  entries.set(key, entry);
};

const evictIfNeeded = (variant) => {
  const cfg = ssrCacheConfig();
  if (variant) {
    // Oldest variant entry goes first so the shared budget stays bounded.
    while (variantCount > cfg.maxVariantEntries) {
      const oldestVariant = Array.from(entries.keys()).find((k) => entries.get(k).variant);
      if (oldestVariant === undefined) break;
      dropEntry(oldestVariant);
      counters.evicted += 1;
    }
  }
  while (entries.size > cfg.maxEntries) {
    const oldest = entries.keys().next().value;
    dropEntry(oldest);
    counters.evicted += 1;
  }
};

export const isStorable = (data) => {
  if (data === null || data === undefined) return false;
  if (typeof data === "object" && Object.keys(data).length === 0) return false;
  return true;
};

const store = (key, data, options) => {
  if (!isStorable(data)) return;
  const cfg = ssrCacheConfig();
  const ttlBase = options.ttlMs !== undefined ? options.ttlMs : cfg.ttlMs;
  const swrMs = options.swrMs !== undefined ? options.swrMs : cfg.swrMs;
  const staleOnErrorMs = options.staleOnError
    ? Math.min(options.staleOnErrorMs !== undefined ? options.staleOnErrorMs : cfg.maxStaleOnErrorMs, cfg.maxStaleOnErrorMs)
    : 0;
  const ttlMs = Math.max(0, Math.floor(ttlBase * (1 - cfg.ttlJitter * Math.random())));
  dropEntry(key);
  const variant = !!options.variant;
  entries.set(key, {
    data,
    at: nowMs(),
    ttlMs,
    swrMs,
    staleOnErrorMs,
    // How long the entry is worth keeping at all.
    retainMs: Math.max(ttlMs + swrMs, staleOnErrorMs),
    scrub: typeof options.scrub === "function" ? options.scrub : null,
    failures: 0,
    troubleFailure: true,
    retryAt: 0,
    variant,
  });
  if (variant) variantCount += 1;
  evictIfNeeded(variant);
};

const backoffMs = (failures, retryAfterSeconds) => {
  const cfg = ssrCacheConfig();
  const exponential = Math.min(
    cfg.refreshBackoffMaxMs,
    cfg.refreshBackoffBaseMs * Math.pow(2, Math.min(Math.max(failures - 1, 0), 10))
  );
  const hinted = retryAfterSeconds > 0
    ? Math.min(cfg.refreshBackoffMaxMs, retryAfterSeconds * 1000)
    : 0;
  return Math.max(exponential, hinted) + Math.floor(Math.random() * 1000);
};

const noteFailure = (key, err) => {
  const now = nowMs();
  const cfg = ssrCacheConfig();
  const retryAfter = getRetryAfterSeconds(err);
  const entry = entries.get(key);
  if (entry) {
    entry.troubleFailure = isBackendTroubleError(err);
    entry.failures += 1;
    // Stops the refresh loop: no new backend call for this key until then.
    entry.retryAt = now + backoffMs(entry.failures, retryAfter);
  }
  if (isRateLimitError(err)) {
    const waitMs = Math.min(
      cfg.rateLimitMaxWaitMs,
      retryAfter > 0 ? retryAfter * 1000 : cfg.rateLimitDefaultWaitMs
    );
    failFast.set(key, { until: now + waitMs, error: err });
    if (failFast.size > 500) {
      failFast.forEach((value, k) => {
        if (value.until <= now) failFast.delete(k);
      });
    }
  }
};

const startFlight = (key, options, loader) => {
  const flight = Promise.resolve()
    .then(() => loader())
    .then(
      (data) => {
        inflight.delete(key);
        failFast.delete(key);
        store(key, data, options);
        return data;
      },
      (err) => {
        inflight.delete(key);
        noteFailure(key, err);
        throw err;
      }
    );
  inflight.set(key, flight);
  return flight;
};

// The copy that may be served because the backend is failing: younger than the
// scope's bound, and the scope allows it at all (prices/promo scopes do not).
const staleOnErrorCandidate = (entry, now) => {
  if (!entry || !(entry.staleOnErrorMs > 0)) return null;
  return now - entry.at <= entry.staleOnErrorMs ? entry : null;
};

const serveStaleOnError = (entry) => {
  bump("staleOnError");
  return entry.scrub ? entry.scrub(entry.data) : entry.data;
};

/**
 * @param {Object}   options
 * @param {string}   options.key             fully normalised key (callers include every input that changes the answer)
 * @param {number}   [options.ttlMs]
 * @param {number}   [options.swrMs]
 * @param {boolean}  [options.staleOnError]  allow serving an expired copy when the loader fails (never for prices/promo)
 * @param {number}   [options.staleOnErrorMs]
 * @param {Function} [options.scrub]         applied to a copy served by stale-on-error (returns a new object)
 * @param {boolean}  [options.variant]       key carries a client-supplied id; uses the smaller variant budget
 * @param {Function} loader                  () => Promise<data>
 */
export const ssrCachedFetch = async (options, loader) => {
  const { key } = options;
  const now = nowMs();
  let entry = entries.get(key);

  if (entry) {
    const age = now - entry.at;
    if (age <= entry.ttlMs) {
      touch(key, entry);
      bump("hit");
      return entry.data;
    }
    if (age <= entry.ttlMs + entry.swrMs) {
      touch(key, entry);
      bump("staleHit");
      if (!inflight.has(key) && now >= entry.retryAt) {
        bump("refresh");
        startFlight(key, options, loader).catch(() => bump("refreshFailed"));
      }
      return entry.data;
    }
    if (age > entry.retainMs) {
      dropEntry(key);
      entry = undefined;
    }
  }

  const stale = staleOnErrorCandidate(entry, now);

  // A copy that is only still around because of stale-on-error, whose last
  // refresh failed: do not ask the failing backend again before its backoff
  // ends (this is the loop that used to hammer a 429-ing backend).
  if (stale && stale.troubleFailure && now < stale.retryAt && !inflight.has(key)) {
    bump("failFast");
    return serveStaleOnError(stale);
  }

  const blocked = failFast.get(key);
  if (blocked && blocked.until > now) {
    bump("failFast");
    if (stale) return serveStaleOnError(stale);
    bump("errorPropagated");
    throw blocked.error;
  }

  let flight = inflight.get(key);
  if (flight) {
    bump("coalesced");
  } else {
    bump("miss");
    flight = startFlight(key, options, loader);
  }
  try {
    return await flight;
  } catch (err) {
    if (stale && isBackendTroubleError(err)) return serveStaleOnError(stale);
    bump("errorPropagated");
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Prices / promo are never served from an expired copy. `scrub` for scopes
// whose stale-on-error copy is allowed but may carry promo fields: a deep copy
// with every price/promo/discount member removed. The original (shared) cache
// entry is never mutated.
// ---------------------------------------------------------------------------
const PRICE_OR_PROMO_KEY = /(^|_)(price|prices|pricing|promo|promos|promotion|discount|discounts|percentage|percent|bogo|voucher|vouchers|plan|plans|package|packages|retail|currency|offer|offers)($|_)/i;

export const stripPriceAndPromoFields = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripPriceAndPromoFields);
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      if (!PRICE_OR_PROMO_KEY.test(k)) {
        out[k] = stripPriceAndPromoFields(value[k]);
      }
    });
    return out;
  }
  return value;
};

// ---------------------------------------------------------------------------
// Visibility: the plan's verification needs "cache hit rate di log pm2".
// One line per 5 minutes, only when something happened, never in tests/browser.
// ---------------------------------------------------------------------------
let statsTimer = null;
let lastLogged = null;

const statsLoggingEnabled = () => (
  typeof window === "undefined"
  && process.env.NODE_ENV === "production"
  && !process.env.JEST_WORKER_ID
  && process.env.SSR_CACHE_STATS_LOG !== "0"
);

export const formatSsrCacheStats = (now, previous) => {
  const delta = (name) => now[name] - (previous ? previous[name] : 0);
  const served = delta("hit") + delta("staleHit");
  const lookups = served + delta("miss") + delta("coalesced");
  const rate = lookups > 0 ? Math.round((served / lookups) * 1000) / 10 : 0;
  return `[ssr-cache] hit_rate=${rate}% hit=${delta("hit")} stale=${delta("staleHit")} miss=${delta("miss")} `
    + `coalesced=${delta("coalesced")} refresh=${delta("refresh")} refresh_failed=${delta("refreshFailed")} `
    + `stale_on_error=${delta("staleOnError")} fail_fast=${delta("failFast")} errors=${delta("errorPropagated")} `
    + `bypass=${delta("bypass")} evicted=${delta("evicted")} size=${now.size} variants=${now.variants}`;
};

function startStatsLogger() {
  if (statsTimer || !statsLoggingEnabled()) return;
  lastLogged = getSsrCacheStats();
  statsTimer = setInterval(() => {
    const current = getSsrCacheStats();
    const active = Object.keys(counters).some((name) => current[name] !== lastLogged[name]);
    if (active) {
      // eslint-disable-next-line no-console
      console.log(formatSsrCacheStats(current, lastLogged));
    }
    lastLogged = current;
  }, 5 * MINUTE_MS);
  if (statsTimer && typeof statsTimer.unref === "function") statsTimer.unref();
}

/** Test hook: forget everything. */
export const __resetSsrCacheForTests = () => {
  entries.clear();
  inflight.clear();
  failFast.clear();
  variantCount = 0;
  Object.keys(counters).forEach((name) => { counters[name] = 0; });
  if (statsTimer) {
    clearInterval(statsTimer);
    statsTimer = null;
  }
};
